// Report Builder admin UI (plan 1.8) — list + editor for org report templates,
// with a live ReportView preview fed sample data.
// See docs/Report-Builder-Implementation-Plan.md.
//
// The stock "Weekly Report" is code-defined: editing it here creates the org's
// copy-on-write override row (slug 'weekly-report'); deleting that row is
// surfaced as "Reset to default", never as delete — so the dropdown on the
// Reports screen always has a valid default.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReportView from '../../report/ReportView';
import ConfirmationModal from '../../ConfirmationModal';
import { Post } from '../../../models/post';
import {
  ATTENDANCE_CHART_META,
  ATTENDANCE_TILE_LABELS,
  AttendanceChartKey,
  AttendanceChartScope,
  AttendanceTileKey,
  DEFAULT_ATTENDANCE_CHARTS,
  DEFAULT_ATTENDANCE_TILES,
  DERIVED_FIELD_LABELS,
  HeaderField,
  ReportTemplate,
  ReportTemplateConfig,
  ReportTimeframe,
  STOCK_WEEKLY_TEMPLATE,
  WEEKLY_REPORT_SLUG,
  validateTemplateConfig,
} from '../../../models/reportTemplate';
import { ReportTemplateDataService } from '../../../services/reportTemplateDataService';
import { AttendanceStats } from '../../../services/attendanceStatsService';
import { useAppState } from '../../../context/AppStateContext';
import { getMonday, getFriday, toDateString } from '../../../services/dateUtils';
import { formatDateFullWithOrdinal } from '../../../services/sharedService';
import './reportTemplateBuilder.scss';

/**
 * Whether a chart can say anything over the template's timeframe. The section
 * makes the same call against real data; doing it here too means the builder
 * greys out a choice rather than letting someone tick a chart that will never
 * appear. 'custom' is treated as multi-week — a free date range is usually
 * longer than a week, and the section still drops the chart if it is not.
 */
const chartAppliesTo = (scope: AttendanceChartScope, timeframe: ReportTimeframe): boolean => {
  if (scope === 'any') return true;
  const singleWeek = timeframe === 'daily' || timeframe === 'weekly';
  return scope === 'single-week' ? singleWeek : !singleWeek;
};

const SCOPE_NOTE: Record<AttendanceChartScope, string> = {
  'any': '',
  'multi-week': 'Multi-week reports only',
  'single-week': 'Daily and weekly reports only',
};

/** Manual presets offered in the Add-field menu (beyond free custom fields). */
const MANUAL_FIELD_PRESETS: Record<string, string> = {
  lead_teacher: 'Lead Teacher / Key Worker',
};

const TIMEFRAME_LABELS: Record<ReportTimeframe, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  custom: 'Custom range',
};

interface EditorDraft {
  /** DB row id; null = unsaved (new custom template, or the stock default
   *  being customised for the first time). */
  id: number | null;
  /** 'weekly-report' = the stock override; null = custom template. */
  slug: string | null;
  name: string;
  config: ReportTemplateConfig;
}

const cloneConfig = (config: ReportTemplateConfig): ReportTemplateConfig =>
  JSON.parse(JSON.stringify(config));

/** Smallest unused custom_<n> key, so fieldValues entries never collide. */
const nextCustomKey = (fields: HeaderField[]): string => {
  let n = 1;
  const used = new Set(fields.map((f) => f.key));
  while (used.has(`custom_${n}`)) n += 1;
  return `custom_${n}`;
};

// ── Sample data for the live preview ────────────────────────────────────────
const samplePost = (id: number, date: string, text: string): Post => ({
  id,
  post_date: date,
  title: 'Sample post',
  created_by: 0,
  created_at: date + 'T10:00:00Z',
  updated_at: date + 'T10:00:00Z',
  text_content: text,
  is_published: true,
  uploads: [],
  child_ids: [0],
});

const previewPeriod = (timeframe: ReportTimeframe): { start: string; end: string } => {
  const now = new Date();
  switch (timeframe) {
    case 'daily': {
      const today = toDateString(now);
      return { start: today, end: today };
    }
    case 'monthly':
      return {
        start: toDateString(new Date(now.getFullYear(), now.getMonth(), 1)),
        end: toDateString(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    case 'custom': {
      const start = new Date(now);
      start.setDate(start.getDate() - 13);
      return { start: toDateString(start), end: toDateString(now) };
    }
    case 'weekly':
    default:
      return { start: toDateString(getMonday(now)), end: toDateString(getFriday(now)) };
  }
};

// Sample attendance stats for the builder preview. `weeks` reflects the
// timeframe so the preview honestly shows what a real report renders: a
// single-week report gets tiles + outcome donut only (charts suppressed);
// a multi-week report gets the full chart set.
const SAMPLE_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const sampleAttendanceStats = (weeks: number, graceMinutes: number): AttendanceStats => {
  const perWeek = 5;
  const weeklyTrend = Array.from({ length: weeks }, (_, i) => ({
    week: `Wk ${i + 1}`,
    rate: [100, 80, 60, 100, 90, 80][i % 6],
    scheduled: perWeek,
    attended: [5, 4, 3, 5, 4, 4][i % 6],
  }));
  const hoursPerWeek = weeklyTrend.map((w) => ({ week: w.week, scheduled: 15, attended: +(w.attended * 3).toFixed(1) }));
  const scheduled = perWeek * weeks;
  const attended = weeklyTrend.reduce((n, w) => n + w.attended, 0);
  const noShow = scheduled - attended;
  // One row per sample occurrence (09:00-12:00 Mon-Fri), so a monthly preview
  // honestly exceeds the timeline chart row cap and hides it, exactly as the
  // real report would.
  const sessionTimeline = weeklyTrend.flatMap((w, wi) =>
    SAMPLE_WEEKDAYS.map((day, di) => {
      const isAttended = di < w.attended;
      return {
        label: weeks > 1 ? `${w.week} ${day}` : day,
        date: `2026-07-${String(6 + wi * 7 + di).padStart(2, '0')}`,
        schedStart: 540,
        schedEnd: 720,
        actualStart: isAttended ? (di === 1 ? 552 : 542) : null,
        actualEnd: isAttended ? (di === 3 ? 700 : 720) : null,
        status: (isAttended ? 'attended' : 'absent') as 'attended' | 'absent' | 'unlogged',
      };
    }),
  );
  return {
    hasSchedule: true,
    sessionsScheduled: scheduled,
    sessionsAttended: attended,
    sessionsNoShow: noShow,
    sessionsUnlogged: 0,
    attendanceRate: Math.round((attended / scheduled) * 100),
    hoursScheduled: 15 * weeks,
    hoursAttended: +(attended * 3).toFixed(1),
    daysAttended: attended,
    noShowReasons: noShow > 0 ? [{ reason: 'Illness', count: Math.ceil(noShow / 2) }, { reason: 'Family', count: Math.floor(noShow / 2) }].filter(r => r.count > 0) : [],
    arrivalsOnTime: Math.max(0, attended - 1),
    arrivalsTotal: attended,
    arrivalsEarly: 0,
    arrivalsLate: attended > 0 ? 1 : 0,
    departuresTotal: attended,
    departuresOnTime: Math.max(0, attended - 1),
    departuresEarly: attended > 0 ? 1 : 0,
    departuresLate: 0,
    avgMinutesLate: attended > 0 ? 12 : 0,
    graceMinutes,
    sessionDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    weeklyTrend,
    hoursPerWeek,
    hoursPerDay: SAMPLE_WEEKDAYS.map(day => ({ day, scheduled: 3 * weeks, attended: +(3 * weeks * 0.8).toFixed(1) })),
    outcome: [
      { name: 'Attended', value: attended, fill: '#26923f' },
      { name: 'No-show', value: noShow, fill: '#b8302f' },
    ].filter(d => d.value > 0),
    byDayOfWeek: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => ({
      day, scheduled: i < 5 ? weeks : 0, attended: i < 5 ? Math.round(weeks * 0.8) : 0,
      rate: i < 5 ? 80 : 0,
    })),
    punctuality: [
      { name: 'Arrival', early: 0, onTime: Math.max(0, attended - 1), late: attended > 0 ? 1 : 0 },
      { name: 'Departure', early: attended > 0 ? 1 : 0, onTime: Math.max(0, attended - 1), late: 0 },
    ],
    sessionTimeline,
  };
};

const ReportTemplateBuilder: React.FC = () => {
  const { selectedOrganisation, orgConfig, activeUser } = useAppState();

  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [draft, setDraft] = useState<EditorDraft | null>(null);
  const [draftDirty, setDraftDirty] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    confirmText: string;
    run: () => Promise<void>;
  } | null>(null);

  const dragIndex = useRef<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setTemplates(await ReportTemplateDataService.listTemplates());
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load report templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  // ── Draft editing helpers ─────────────────────────────────────────────────
  const updateDraft = (mutate: (d: EditorDraft) => void) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next: EditorDraft = { ...prev, config: cloneConfig(prev.config) };
      mutate(next);
      return next;
    });
    setDraftDirty(true);
  };

  const updateField = (index: number, mutate: (f: HeaderField) => void) =>
    updateDraft((d) => { mutate(d.config.headerFields[index]); });

  const openEditor = (template: ReportTemplate) => {
    setDraft({
      id: template.id,
      slug: template.slug,
      name: template.name,
      config: cloneConfig(template.config),
    });
    setDraftDirty(false);
    setSaveErrors([]);
    setStatus(null);
  };

  const openNew = () => {
    openEditor({
      id: null,
      slug: null,
      name: '',
      config: cloneConfig(STOCK_WEEKLY_TEMPLATE.config),
    });
  };

  const openDuplicate = (template: ReportTemplate) => {
    openEditor({
      id: null,
      slug: null,
      name: `${template.name} (copy)`,
      config: cloneConfig(template.config),
    });
    setDraftDirty(true);
  };

  const closeEditor = () => {
    setDraft(null);
    setDraftDirty(false);
    setSaveErrors([]);
  };

  const handleSave = async () => {
    if (!draft) return;
    const errors = [
      ...(!draft.name.trim() ? ['The template needs a name.'] : []),
      ...validateTemplateConfig(draft.config),
    ];
    setSaveErrors(errors);
    if (errors.length > 0) return;

    setSaving(true);
    try {
      if (draft.id !== null) {
        await ReportTemplateDataService.updateTemplate(draft.id, {
          name: draft.name.trim(),
          config: draft.config,
        });
      } else {
        await ReportTemplateDataService.createTemplate({
          name: draft.name.trim(),
          config: draft.config,
          slug: draft.slug ?? undefined,
        });
      }
      setStatus(`Saved "${draft.name.trim()}".`);
      closeEditor();
      await load();
    } catch (err: any) {
      setSaveErrors([err?.response?.data?.error ?? err?.message ?? 'Failed to save the template']);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (template: ReportTemplate) => {
    const isOverride = template.slug === WEEKLY_REPORT_SLUG;
    setConfirmAction({
      title: isOverride ? 'Reset to default' : 'Delete template',
      message: isOverride
        ? `Reset "${template.name}" to the built-in Weekly Report? Your customisations will be removed. Existing reports keep the name they were generated with.`
        : `Delete "${template.name}"? Existing reports generated from it keep their PDFs and name; regenerating them will fall back to the default Weekly Report.`,
      confirmText: isOverride ? 'Reset' : 'Delete',
      run: async () => {
        await ReportTemplateDataService.deleteTemplate(template.id!);
        setStatus(isOverride ? 'Weekly Report reset to the built-in default.' : `Deleted "${template.name}".`);
        await load();
      },
    });
  };

  // ── Field list operations ─────────────────────────────────────────────────
  const addDerivedField = (key: string) =>
    updateDraft((d) => {
      d.config.headerFields.push({ key, label: DERIVED_FIELD_LABELS[key], source: 'derived' });
    });

  const addManualField = (key: string, label: string) =>
    updateDraft((d) => {
      d.config.headerFields.push({
        key,
        label,
        source: 'manual',
        defaultValue: '',
        editableAtGeneration: true,
        required: false,
      });
    });

  const removeField = (index: number) =>
    updateDraft((d) => { d.config.headerFields.splice(index, 1); });

  const moveField = (from: number, to: number) =>
    updateDraft((d) => {
      const [f] = d.config.headerFields.splice(from, 1);
      d.config.headerFields.splice(to, 0, f);
    });

  const handleAddSelect = (value: string) => {
    if (!value) return;
    if (value === '__custom__') {
      addManualField(nextCustomKey(draft!.config.headerFields), '');
    } else if (MANUAL_FIELD_PRESETS[value]) {
      addManualField(value, MANUAL_FIELD_PRESETS[value]);
    } else {
      addDerivedField(value);
    }
  };

  // ── Content sections ──────────────────────────────────────────────────────
  const addSection = (type: 'posts' | 'attendance') =>
    updateDraft((d) => {
      if (d.config.sections.some(s => s.type === type)) return;
      d.config.sections.push(
        type === 'posts'
          ? { type: 'posts', includeImages: true }
          : { type: 'attendance', charts: true, stats: true },
      );
    });

  const removeSection = (index: number) =>
    updateDraft((d) => { d.config.sections.splice(index, 1); });

  /**
   * Toggle one tile/chart key. An absent list means "the defaults" — the first
   * edit materialises that default set so the template records an explicit
   * choice from then on, and untouched templates keep rendering as they were.
   */
  const toggleAttendanceKey = (
    index: number,
    group: 'tiles' | 'chartKeys',
    key: AttendanceTileKey | AttendanceChartKey,
    on: boolean,
  ) =>
    updateDraft((d) => {
      const s = d.config.sections[index];
      if (s.type !== 'attendance') return;
      const current: string[] =
        s[group] ?? (group === 'tiles' ? DEFAULT_ATTENDANCE_TILES : DEFAULT_ATTENDANCE_CHARTS);
      const next = on
        ? (current.includes(key) ? current : [...current, key])
        : current.filter(k => k !== key);
      if (group === 'tiles') s.tiles = next as AttendanceTileKey[];
      else s.chartKeys = next as AttendanceChartKey[];
    });

  // ── Live preview ──────────────────────────────────────────────────────────
  const preview = useMemo(() => {
    if (!draft) return null;
    const period = previewPeriod(draft.config.timeframe);
    const posts = [
      samplePost(1, period.start, 'Alex settled quickly this morning and joined the group activity with growing confidence.'),
      samplePost(2, period.end, 'Alex worked one-to-one on turn-taking games and was proud to show the finished result.'),
    ];
    // Sample values so every row renders; manual-editable fields with no
    // default show a ghost marker — the real report omits rows left empty.
    const fieldValues: Record<string, string> = {};
    draft.config.headerFields.forEach((f) => {
      if (f.source === 'manual' && !f.defaultValue?.trim()) {
        fieldValues[f.key] = f.editableAtGeneration ? '(entered at generation)' : '';
      }
    });
    // Weeks spanned by the sample period → how many chart data points the
    // preview shows (weekly = 1 → tiles/donut only; monthly/custom = multi).
    const dayspan = Math.round(
      (new Date(period.end + 'T00:00').getTime() - new Date(period.start + 'T00:00').getTime()) / 86400000,
    );
    const weeks = Math.max(1, Math.ceil((dayspan + 1) / 7));
    const attendance = sampleAttendanceStats(weeks, orgConfig?.attendanceGraceMinutes ?? 5);
    return { period, posts, fieldValues, attendance };
  }, [draft]);

  // ── Renderers ─────────────────────────────────────────────────────────────
  const listView = (
    <div className="rtb-list">
      <div className="pill-toolbar" role="toolbar" aria-label="Report templates">
        <h3 className="rtb-heading">Report Templates</h3>
        <div className="pill-toolbar__spacer" />
        <button type="button" className="btn btn-brand btn-sm" data-help="rtb.new" onClick={openNew}>
          <i className="fas fa-plus me-1" /> New template
        </button>
      </div>

      {status && <div className="alert alert-success mx-4 mt-3 mb-0 py-2">{status}</div>}
      {error && <div className="alert alert-danger mx-4 mt-3 mb-0 py-2">{error}</div>}

      <div className="rtb-table-wrap">
        <table className="rtb-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Timeframe</th>
              <th>Header fields</th>
              <th className="rtb-th--right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="rtb-empty">Loading templates…</td></tr>
            )}
            {!loading && templates.map((t) => {
              const isStock = t.id === null;
              const isOverride = t.slug === WEEKLY_REPORT_SLUG && t.id !== null;
              return (
                <tr key={t.id ?? 'stock'}>
                  <td>
                    <span className="rtb-name">{t.name}</span>
                    {isStock && <span className="rtb-badge" title="Built-in default — editing it creates your organisation's own copy">Default</span>}
                    {isOverride && <span className="rtb-badge rtb-badge--custom" title="Your organisation's customised copy of the built-in default">Customised default</span>}
                  </td>
                  <td>{TIMEFRAME_LABELS[t.config.timeframe]}</td>
                  <td>{t.config.headerFields.map((f) => f.label).join(', ') || '—'}</td>
                  <td className="rtb-td--right">
                    <button type="button" className="rtb-action" onClick={() => openEditor(t)} title="Edit">
                      <i className="fas fa-pen" /> Edit
                    </button>
                    <button type="button" className="rtb-action" onClick={() => openDuplicate(t)} title="Duplicate into a new template">
                      <i className="fas fa-copy" /> Duplicate
                    </button>
                    {isOverride && (
                      <button type="button" className="rtb-action rtb-action--danger" onClick={() => handleDelete(t)} title="Remove your customisations and return to the built-in default">
                        <i className="fas fa-rotate-left" /> Reset to default
                      </button>
                    )}
                    {!isStock && !isOverride && (
                      <button type="button" className="rtb-action rtb-action--danger" onClick={() => handleDelete(t)} title="Delete">
                        <i className="fas fa-trash" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const editorView = draft && (
    <div className="rtb-editor">
      <div className="pill-toolbar" role="toolbar" aria-label="Template editor">
        <button type="button" className="rtb-back" onClick={closeEditor} title="Back to templates">
          <i className="fas fa-arrow-left" /> Templates
        </button>
        <h3 className="rtb-heading">
          {draft.id !== null ? `Edit "${draft.name}"` : draft.slug === WEEKLY_REPORT_SLUG ? 'Customise Weekly Report' : 'New template'}
        </h3>
        <div className="pill-toolbar__spacer" />
        <button type="button" className="btn btn-outline-secondary btn-sm me-2" onClick={closeEditor}>Cancel</button>
        <button type="button" className="btn btn-brand btn-sm" data-help="rtb.save" onClick={() => void handleSave()} disabled={saving || !draftDirty}>
          {saving ? 'Saving…' : 'Save template'}
        </button>
      </div>

      {saveErrors.length > 0 && (
        <div className="alert alert-danger mx-4 mt-3 mb-0 py-2">
          <ul className="mb-0 ps-3">
            {saveErrors.map((e) => <li key={e}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="rtb-editor-panes">
        {/* ── Left: the form ── */}
        <div className="rtb-form">
          <div className="rtb-form-section-title">Details</div>
          <div className="mb-3">
            <label className="form-label">Template name</label>
            <input
              type="text"
              className="form-control"
              value={draft.name}
              placeholder="e.g. Monthly LA Summary"
              onChange={(e) => { setDraft({ ...draft, name: e.target.value }); setDraftDirty(true); }}
            />
          </div>

          <div className="mb-3" data-help="rtb.timeframe">
            <label className="form-label">Timeframe</label>
            <select
              className="form-select"
              value={draft.config.timeframe}
              onChange={(e) => updateDraft((d) => { d.config.timeframe = e.target.value as ReportTimeframe; })}
            >
              {(Object.keys(TIMEFRAME_LABELS) as ReportTimeframe[]).map((tf) => (
                <option key={tf} value={tf}>{TIMEFRAME_LABELS[tf]}</option>
              ))}
            </select>
            <small className="text-muted">Drives the period selector on the Reports screen.</small>
          </div>

          <div className="rtb-form-section-title">Header</div>
          <div className="mb-3">
            <label>
              <input
                type="checkbox"
                checked={draft.config.headerLogo !== 'none'}
                onChange={(e) => updateDraft((d) => { d.config.headerLogo = e.target.checked ? 'org' : 'none'; })}
              /> Include organisation logo
            </label>
            <small className="text-muted d-block">
              {selectedOrganisation?.logoUrl
                ? 'Uses the logo from Admin → Organisation. Turn off for de-identified reports.'
                : 'No logo uploaded yet — set one in Admin → Organisation.'}
            </small>
          </div>
          <div className="mb-3">
            <label className="form-label">Header layout</label>
            <div className="rtb-segment" role="group" aria-label="Header columns" style={{marginLeft:10}}>
              {[1, 2].map((cols) => (
                <button
                  key={cols}
                  type="button"
                  className={draft.config.headerColumns === cols ? 'is-active' : ''}
                  onClick={() => updateDraft((d) => { d.config.headerColumns = cols as 1 | 2; })}
                >
                  {cols} column{cols === 2 ? 's' : ''}
                </button>
              ))}
            </div>
          </div>
          <small className="text-muted d-block mb-2">Drag and drop to change the order.</small>
          <div className="rtb-fields">
            {draft.config.headerFields.length === 0 && (
              <div className="rtb-fields-empty">
                {draft.config.headerLogo !== 'none' && selectedOrganisation?.logoUrl
                  ? 'No header fields — the report will show only the logo above the posts.'
                  : 'No header fields — the report will start straight at the posts.'}
              </div>
            )}
            {draft.config.headerFields.map((field, i) => (
              <div
                key={`${field.key}-${i}`}
                className="rtb-field"
                draggable
                onDragStart={() => { dragIndex.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex.current !== null && dragIndex.current !== i) moveField(dragIndex.current, i);
                  dragIndex.current = null;
                }}
              >
                <span className="rtb-field__grip" title="Drag to reorder"><i className="fas fa-grip-vertical" /></span>
                <div className="rtb-field__main">
                  <div className="rtb-field__top">
                    <input
                      type="text"
                      className="form-control form-control-sm rtb-field__label"
                      value={field.label}
                      placeholder="Field label"
                      aria-label="Field label"
                      onChange={(e) => updateField(i, (f) => { f.label = e.target.value; })}
                    />
                    {field.source === 'derived' ? (
                      <span className="rtb-badge" title={`Derived from app data (${field.key})`}>
                        <i className="fas fa-bolt me-1" />computed automatically
                      </span>
                    ) : (
                      <input
                        type="text"
                        className="form-control form-control-sm rtb-field__default"
                        value={field.defaultValue ?? ''}
                        placeholder={field.editableAtGeneration ? 'Default value (optional)' : 'Fixed value (required)'}
                        aria-label="Default value"
                        onChange={(e) => updateField(i, (f) => { f.defaultValue = e.target.value; })}
                      />
                    )}
                  </div>
                  <div className="rtb-field__flags">
                    {/* Both sources: a derived editable field pre-fills the
                        generation input with its computed value (e.g. correct
                        a pupil's name on one report without editing data). */}
                    <label title={field.source === 'derived' ? 'Shows the computed value in an input at generation, so it can be corrected for one report' : undefined}>
                      <input
                        type="checkbox"
                        checked={!!field.editableAtGeneration}
                        onChange={(e) => updateField(i, (f) => {
                          f.editableAtGeneration = e.target.checked;
                          if (!e.target.checked) f.required = false;
                        })}
                      /> Editable at generation
                    </label>
                    <label className={!field.editableAtGeneration ? 'rtb-flag--disabled' : ''}>
                      <input
                        type="checkbox"
                        checked={!!field.required}
                        disabled={!field.editableAtGeneration}
                        onChange={(e) => updateField(i, (f) => { f.required = e.target.checked; })}
                      /> Required
                    </label>
                    {draft.config.headerColumns === 2 && (
                      <label>
                        <input
                          type="checkbox"
                          checked={!!field.fullWidth}
                          onChange={(e) => updateField(i, (f) => { f.fullWidth = e.target.checked; })}
                        /> Full width
                      </label>
                    )}
                  </div>
                </div>
                <button type="button" className="rtb-field__remove" onClick={() => removeField(i)} title="Remove field" aria-label={`Remove ${field.label || 'field'}`}>
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>

          <div className="mb-4 mt-2">
            <select
              className="form-select form-select-sm rtb-add-field"
              value=""
              aria-label="Add header field"
              onChange={(e) => handleAddSelect(e.target.value)}
            >
              <option value="">+ Add field…</option>
              <optgroup label="Computed automatically">
                {Object.entries(DERIVED_FIELD_LABELS)
                  .filter(([key]) => !draft.config.headerFields.some((f) => f.key === key))
                  .map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </optgroup>
              <optgroup label="Entered by you">
                {Object.entries(MANUAL_FIELD_PRESETS)
                  .filter(([key]) => !draft.config.headerFields.some((f) => f.key === key))
                  .map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                <option value="__custom__">Custom field…</option>
              </optgroup>
            </select>
          </div>

          <div className="rtb-form-section-title">Content</div>
          <div className="rtb-sections">
            {draft.config.sections.map((section, i) => section.type === 'posts' ? (
              <div key={i} className="rtb-section">
                <span className="rtb-section__name"><i className="fas fa-newspaper me-2" />Published posts</span>
                <div className="rtb-section__controls">
                  <label>
                    <input
                      type="checkbox"
                      checked={section.includeImages}
                      onChange={(e) => updateDraft((d) => {
                        const s = d.config.sections[i];
                        if (s.type === 'posts') s.includeImages = e.target.checked;
                      })}
                    /> Include photos
                  </label>
                  <button type="button" className="rtb-section__remove" title="Remove section" onClick={() => removeSection(i)}>
                    <i className="fas fa-times" />
                  </button>
                </div>
              </div>
            ) : (
              <div key={i} className="rtb-section rtb-section--stacked">
                <div className="rtb-section__head">
                  <span className="rtb-section__name"><i className="fas fa-calendar-check me-2" />Attendance</span>
                  <div className="rtb-section__controls" data-help="rtb.attendance-toggles">
                    <label>
                      <input
                        type="checkbox"
                        checked={section.stats}
                        onChange={(e) => updateDraft((d) => {
                          const s = d.config.sections[i];
                          if (s.type === 'attendance') s.stats = e.target.checked;
                        })}
                      /> Stats
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={section.charts}
                        onChange={(e) => updateDraft((d) => {
                          const s = d.config.sections[i];
                          if (s.type === 'attendance') s.charts = e.target.checked;
                        })}
                      /> Charts
                    </label>
                    <button type="button" className="rtb-section__remove" title="Remove section" onClick={() => removeSection(i)}>
                      <i className="fas fa-times" />
                    </button>
                  </div>
                </div>

                {section.stats && (
                  <div className="rtb-picker">
                    <div className="rtb-picker__title">Stats to show</div>
                    <div className="rtb-picker__options">
                      {(Object.keys(ATTENDANCE_TILE_LABELS) as AttendanceTileKey[]).map((key) => (
                        <label key={key} className="rtb-picker__option">
                          <input
                            type="checkbox"
                            checked={(section.tiles ?? DEFAULT_ATTENDANCE_TILES).includes(key)}
                            onChange={(e) => toggleAttendanceKey(i, 'tiles', key, e.target.checked)}
                          />
                          <span>{ATTENDANCE_TILE_LABELS[key]}</span>
                        </label>
                      ))}
                    </div>
                    <small className="text-muted">A stat with no data behind it is left out of the report.</small>
                  </div>
                )}

                {section.charts && (
                  <div className="rtb-picker">
                    <div className="rtb-picker__title" data-help="rtb.charts-picker">Charts to show</div>
                    <div className="rtb-picker__options">
                      {ATTENDANCE_CHART_META.map((chart) => {
                        const applies = chartAppliesTo(chart.scope, draft.config.timeframe);
                        return (
                          <label
                            key={chart.key}
                            className={`rtb-picker__option${applies ? '' : ' rtb-picker__option--disabled'}`}
                            title={applies ? undefined : SCOPE_NOTE[chart.scope]}
                          >
                            <input
                              type="checkbox"
                              disabled={!applies}
                              checked={applies && (section.chartKeys ?? DEFAULT_ATTENDANCE_CHARTS).includes(chart.key)}
                              onChange={(e) => toggleAttendanceKey(i, 'chartKeys', chart.key, e.target.checked)}
                            />
                            <span>
                              {chart.label}
                              {!applies && <em className="rtb-picker__note">{SCOPE_NOTE[chart.scope]}</em>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="rtb-section-add">
              {!draft.config.sections.some(s => s.type === 'posts') && (
                <button type="button" className="rtb-action" onClick={() => addSection('posts')}>
                  <i className="fas fa-plus" /> Posts
                </button>
              )}
              {!draft.config.sections.some(s => s.type === 'attendance') && (
                <button type="button" className="rtb-action" data-help="rtb.add-attendance" onClick={() => addSection('attendance')}>
                  <i className="fas fa-plus" /> Attendance
                </button>
              )}
            </div>
            <small className="text-muted">Greyed-out charts do not suit this report&rsquo;s timeframe. A chart with no data behind it is left out of the report.</small>
          </div>
        </div>

        {/* ── Right: live preview ── */}
        <div className="rtb-preview" data-help="rtb.preview">
          <div className="rtb-preview__bar">
            <span><i className="fas fa-eye me-2" />Preview with sample data</span>
            <small>Rows left empty at generation are omitted from the real report.</small>
          </div>
          <div className="rtb-preview__page">
            {preview && (
              <ReportView
                posts={preview.posts}
                childName="Alex Sample"
                reportDate={formatDateFullWithOrdinal(new Date().toISOString(), false)}
                startDate={preview.period.start}
                endDate={preview.period.end}
                includeHeader
                logoUrl={selectedOrganisation?.logoUrl}
                orgName={orgConfig?.settingName || selectedOrganisation?.name}
                authorName={activeUser?.name}
                brandColour={selectedOrganisation?.brandColour}
                template={draft.config}
                templateName={draft.name.trim() || 'Untitled report'}
                fieldValues={preview.fieldValues}
                attendance={preview.attendance}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="rtb" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {draft ? editorView : listView}

      <ConfirmationModal
        show={confirmAction !== null}
        title={confirmAction?.title ?? ''}
        message={confirmAction?.message ?? ''}
        confirmText={confirmAction?.confirmText}
        cancelText="Cancel"
        onConfirm={() => {
          const action = confirmAction;
          setConfirmAction(null);
          if (action) {
            action.run().catch((err: any) =>
              setError(err?.response?.data?.error ?? err?.message ?? 'Action failed'));
          }
        }}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
};

export default ReportTemplateBuilder;
